import type { ReactElement } from 'react';
import type { GetStaticProps, GetStaticPropsContext } from 'next';
import type { CategoriesViewProps, CategoryRequestResultProps } from '../types';

import * as React from 'react';

import { View } from '../generated/categories';
// import { CollectionsA as View } from '../containers/CollectionsA';
import { BaseLayout } from '../layouts/BaseLayout';

import { NextSeo, CollectionPageJsonLd, BreadcrumbJsonLd } from 'next-seo';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { getAllCollections } from '../app/api';
import { mappingCollection } from '../app/mappings';
import { useAppContext } from '../contexts/AppContext';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { cleanSlug, getLocalizedPaths, getLocalizedSlugs } from '../utils/localize-helpers';
import { CollectionTypesEnum } from '../types/enum';
import { translateUrl } from 'next-translate-routes';
import { getNonPageNamespaces } from '../utils/get-non-page-namespaces';

export default function CategoriesPage(props: CategoriesViewProps) {
  const { categories, pageContext } = props;
  const { t, i18n } = useTranslation(['common', 'categories']);
  const appContext = useAppContext();
  const { name, assetAbsoluteUrl } = appContext.useConfig();
  const router = useRouter();
  const pageContextWithPaths = {
    ...pageContext,
    paths: getLocalizedPaths(router),
    slugs: getLocalizedSlugs(router),
    slug: cleanSlug(router.pathname),
  };

  return (
    <>
      <NextSeo
        title={
          i18n.exists('categories:title', { fallbackLng: pageContextWithPaths.locale })
            ? t('categories:title')
            : t('common:title')
        }
        description={
          i18n.exists('categories:description', { fallbackLng: pageContextWithPaths.locale })
            ? t('categories:description')
            : t('common:description')
        }
      />
      <CollectionPageJsonLd
        name={t('common:title')}
        hasPart={categories.map((category) => ({
          about: category.description,
          author: { name },
          name: category.name,
          audience: 'Internet',
          keywords: 'schema',
          thumbnailUrl: `${assetAbsoluteUrl}${
            category.images.cover1x1 || category.images.cover
          }/?ar[r]=1x1&ar[w]=512&fmt=.png`,
          image: `${assetAbsoluteUrl}${category.images.cover1x1 || category.images.cover}/`,
        }))}
      />
      <BreadcrumbJsonLd
        itemListElements={pageContextWithPaths.breadcrumbs.map((crumb, index) => ({
          position: index + 1,
          name: crumb.name,
          item: crumb.href,
        }))}
      />
      <View
        {...props}
        collections={categories}
        title={
          i18n.exists('categories:headline', { fallbackLng: pageContextWithPaths.locale })
            ? t('categories:headline')
            : t('common:headline')
        }
        body={
          i18n.exists('categories:body', { fallbackLng: pageContextWithPaths.locale })
            ? t('categories:body')
            : t('common:body')
        }
        pageContext={pageContextWithPaths}
      />
    </>
  );
}

// Adding getLayout property. Allowing us to return a React component for the layout.
// This allows us to define the layout on a per-page basis. Since we're returning a function,
// we can have complex nested layouts if desired.
CategoriesPage.getLayout = function getLayout(page: ReactElement) {
  const { props } = page;
  const { pageContext } = props;
  return (
    <BaseLayout pageType="categories" pageContext={pageContext}>
      {page}
    </BaseLayout>
  );
};

export const getStaticProps: GetStaticProps = async (context: GetStaticPropsContext) => {
  const url = `${process.env.PROJECT_URL?.replace(/\/+$/, '')}`;
  const locale: string = (context.locale || context.defaultLocale) as string;
  const categories: CategoryRequestResultProps[] | undefined = await getAllCollections(
    CollectionTypesEnum.CATEGORY,
    {
      _fieldsGame: 'image',
      _gameLimit: 12,
      _limit: 8,
    },
    {
      ssr: true,
      locale,
    }
  );

  if (!categories) return { notFound: true };

  const page = {
    title: null,
    body: null,
    locale: context.locale,
  };
  const { title, body } = page;
  const pageContext = {
    locale: page.locale,
    locales: context.locales,
    defaultLocale: context.defaultLocale,
    slugs: [],
    slug: 'categories',
    breadcrumbs: [
      { href: url, path: '/', name: 'Home' },
      {
        href: `${url}${translateUrl('/categories', locale)}`,
        path: `${translateUrl('/categories', locale)}`,
        name: 'Categories', // Todo: HUZ2ERA-1112 - Translate crumb name on server side?
      },
    ],
  };

  return {
    props: {
      title,
      body,
      categories: categories.map((category: CategoryRequestResultProps) => ({
        ...mappingCollection(category),
      })),
      global: {
        popular: [],
        latest: [],
      },
      pageContext,
      // Get our translations by namespace and locale.
      // https://github.com/isaachinman/next-i18next#serversidetranslations
      ...(await serverSideTranslations(locale as string, ['common', 'categories', ...getNonPageNamespaces(locale)])),
    },
    // Incremental Static Regeneration (ISR).
    // https://nextjs.org/docs/basic-features/data-fetching/incremental-static-regeneration
    revalidate: parseInt(process.env.PROJECT_SSR_REVALIDATE_SECONDS || '') || 60,
  };
};
